import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  createStoredCountryOption,
  getCountryOptionsForLanguages,
  SUPPORTED_POPUP_LANGUAGES
} from '@constants/countryTargeting';
import './CountryTargeting.css';

const emptyIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const CountryTargeting = ({
  mode,
  countryCodes,
  onModeChange,
  onCountryCodesChange,
  error
}) => {
  const [selectedLanguageCodes, setSelectedLanguageCodes] = useState([]);

  const selectedLanguages = useMemo(() => (
    SUPPORTED_POPUP_LANGUAGES.filter(language => (
      selectedLanguageCodes.includes(language.normalizedCode)
    ))
  ), [selectedLanguageCodes]);

  const allCountryOptions = useMemo(
    () => getCountryOptionsForLanguages(SUPPORTED_POPUP_LANGUAGES),
    []
  );

  const filteredCountryOptions = useMemo(
    () => getCountryOptionsForLanguages(selectedLanguages),
    [selectedLanguages]
  );

  const selectedOptions = useMemo(() => {
    const optionByCode = new Map(allCountryOptions.map(option => [option.code, option]));
    const storedOptions = countryCodes
      .filter(code => !optionByCode.has(code))
      .map(createStoredCountryOption);
    storedOptions.forEach(option => optionByCode.set(option.code, option));
    return countryCodes.map(code => optionByCode.get(code));
  }, [allCountryOptions, countryCodes]);

  const handleSelectionChange = (_event, selected) => {
    onCountryCodesChange(selected.map(country => country.code));
  };

  const handleSelectFilteredCountries = () => {
    onCountryCodesChange(Array.from(new Set([
      ...countryCodes,
      ...filteredCountryOptions.map(option => option.code)
    ])));
  };

  return (
    <section className="country-targeting-card">
      <div className="country-targeting-heading">
        <span className="country-targeting-icon">
          <PublicIcon />
        </span>
        <div>
          <Typography className="country-targeting-title">Quốc gia hiển thị</Typography>
          <Typography className="country-targeting-description">
            Giới hạn popup theo vị trí quốc gia của người xem.
          </Typography>
        </div>
      </div>

      <RadioGroup
        value={mode}
        onChange={(event) => onModeChange(event.target.value)}
        className="country-targeting-mode"
      >
        <FormControlLabel
          value="all"
          control={<Radio size="small" />}
          label={
            <span>
              <strong>Tất cả quốc gia</strong>
              <small>Phù hợp với thông báo chung</small>
            </span>
          }
        />
        <FormControlLabel
          value="include"
          control={<Radio size="small" />}
          label={
            <span>
              <strong>Chỉ các quốc gia được chọn</strong>
              <small>Nhắm đúng thị trường cần hiển thị</small>
            </span>
          }
        />
      </RadioGroup>

      {mode === 'include' && (
        <Box className="country-targeting-picker">
          <div className="country-targeting-field-heading">
            <span>1</span>
            <div>
              <Typography>Chọn ngôn ngữ để lọc</Typography>
              <small>Có {SUPPORTED_POPUP_LANGUAGES.length} ngôn ngữ được hệ thống hỗ trợ</small>
            </div>
          </div>

          <Autocomplete
            multiple
            disableCloseOnSelect
            limitTags={3}
            options={SUPPORTED_POPUP_LANGUAGES}
            value={selectedLanguages}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            isOptionEqualToValue={(option, value) => option.normalizedCode === value.normalizedCode}
            onChange={(_event, selected) => {
              setSelectedLanguageCodes(selected.map(language => language.normalizedCode));
            }}
            noOptionsText="Không tìm thấy ngôn ngữ"
            renderOption={(props, option, { selected }) => (
              <li {...props} className={`${props.className || ''} country-targeting-option`}>
                <Checkbox
                  icon={emptyIcon}
                  checkedIcon={checkedIcon}
                  checked={selected}
                  size="small"
                />
                <span className="country-targeting-option-name">{option.name}</span>
                <span className="country-targeting-code">{option.code}</span>
              </li>
            )}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.normalizedCode}
                label={`${option.name} · ${option.code}`}
                size="small"
              />
            ))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tìm và chọn ngôn ngữ"
                placeholder={selectedLanguages.length === 0 ? 'Ví dụ: Tiếng Việt, en, zh-TW...' : ''}
                helperText="Có thể chọn nhiều ngôn ngữ để gộp danh sách quốc gia."
              />
            )}
          />

          <div className="country-targeting-field-heading country-targeting-country-heading">
            <span>2</span>
            <div>
              <Typography>Chọn quốc gia hiển thị popup</Typography>
              <small>
                {selectedLanguages.length > 0
                  ? `${filteredCountryOptions.length} quốc gia phù hợp với bộ lọc`
                  : 'Chọn ít nhất một ngôn ngữ ở bước trên'}
              </small>
            </div>
          </div>

          <div className="country-targeting-picker-toolbar">
            <Typography>
              Đã chọn <strong>{countryCodes.length}</strong> quốc gia
            </Typography>
            <div>
              <Button
                size="small"
                onClick={handleSelectFilteredCountries}
                disabled={filteredCountryOptions.length === 0}
              >
                Chọn tất cả kết quả
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => onCountryCodesChange([])}
                disabled={countryCodes.length === 0}
              >
                Bỏ chọn
              </Button>
            </div>
          </div>

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={filteredCountryOptions}
            value={selectedOptions}
            disabled={selectedLanguages.length === 0}
            groupBy={(option) => `${option.languageName} · ${option.languageCode.toUpperCase()}`}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            onChange={handleSelectionChange}
            noOptionsText="Không có quốc gia phù hợp với bộ lọc ngôn ngữ"
            renderOption={(props, option, { selected }) => (
              <li {...props} className={`${props.className || ''} country-targeting-option`}>
                <Checkbox
                  icon={emptyIcon}
                  checkedIcon={checkedIcon}
                  checked={selected}
                  size="small"
                />
                <span className="country-targeting-flag">{option.flag}</span>
                <span className="country-targeting-option-name">{option.name}</span>
                <span className="country-targeting-code">{option.code}</span>
              </li>
            )}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.code}
                label={`${option.flag} ${option.name}`}
                size="small"
              />
            ))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tìm và chọn quốc gia"
                placeholder={countryCodes.length === 0 ? 'Ví dụ: Việt Nam, Nhật Bản...' : ''}
                error={Boolean(error)}
                helperText={error || 'Chỉ hiện quốc gia thuộc các ngôn ngữ đã chọn.'}
              />
            )}
          />
        </Box>
      )}
    </section>
  );
};

export default CountryTargeting;
